/**
 * Enhanced Side Panel with improved UX
 */

import "./enhanced-side-panel.scss";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import {
  RiSidebarFoldLine,
  RiSidebarUnfoldLine,
  RiSearchLine,
  RiCloseLine,
} from "react-icons/ri";
import Select from "react-select";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { useLoggerStore } from "../../lib/store-logger";
import Logger, { LoggerFilterType } from "../logger/Logger";
import { Button } from "../ui/Button";

const filterOptions = [
  { value: "conversations", label: "💬 Conversations" },
  { value: "tools", label: "🔧 Tool Use" },
  { value: "none", label: "📋 All Logs" },
];

export default function EnhancedSidePanel() {
  const { connected, client } = useLiveAPIContext();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const loggerRef = useRef<HTMLDivElement>(null);
  const loggerLastHeightRef = useRef<number>(-1);
  const { log, logs, clearLogs } = useLoggerStore();

  const [textInput, setTextInput] = useState("");
  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
  } | null>(filterOptions[2]); // Default to "All Logs"
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (loggerRef.current) {
      const el = loggerRef.current;
      const scrollHeight = el.scrollHeight;
      if (scrollHeight !== loggerLastHeightRef.current) {
        el.scrollTop = scrollHeight;
        loggerLastHeightRef.current = scrollHeight;
      }
    }
  }, [logs]);

  // Listen for log events
  useEffect(() => {
    client.on("log", log);
    return () => {
      client.off("log", log);
    };
  }, [client, log]);

  const handleSubmit = () => {
    if (!textInput.trim()) return;

    client.send([{ text: textInput }]);
    setTextInput("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const filteredLogs = logs.filter((logEntry) => {
    if (!searchQuery) return true;
    const message =
      typeof logEntry.message === "string"
        ? logEntry.message
        : JSON.stringify(logEntry.message);
    return message.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className={cn("enhanced-side-panel", { open })}>
      {/* Header */}
      <header className="panel-header">
        <div className="header-content">
          <h2>Live API Console</h2>
          <div className="header-actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Collapse panel" : "Expand panel"}
              leftIcon={open ? <RiSidebarFoldLine /> : <RiSidebarUnfoldLine />}
            />
          </div>
        </div>
      </header>

      {/* Status and Controls */}
      <section className="panel-status">
        <div className="connection-status">
          <div className={cn("status-badge", { connected })}>
            <span className="status-dot" />
            <span className="status-text">
              {connected ? "🟢 Connected" : "🔴 Disconnected"}
            </span>
          </div>
        </div>

        {/* Filter and Search Controls */}
        <div className="panel-controls">
          <div className="filter-section">
            <label htmlFor="log-filter" className="control-label">
              Filter Logs:
            </label>
            <Select
              id="log-filter"
              className="react-select"
              classNamePrefix="react-select"
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  background: "var(--Neutral-15)",
                  color: "var(--Neutral-90)",
                  minHeight: "36px",
                  border: state.isFocused
                    ? "1px solid var(--Blue-500)"
                    : "1px solid var(--Neutral-30)",
                  boxShadow: state.isFocused
                    ? "0 0 0 1px var(--Blue-500)"
                    : "none",
                  "&:hover": {
                    borderColor: "var(--Neutral-50)",
                  },
                }),
                option: (styles, { isFocused, isSelected }) => ({
                  ...styles,
                  backgroundColor: isFocused
                    ? "var(--Neutral-30)"
                    : isSelected
                      ? "var(--Blue-800)"
                      : "var(--Neutral-15)",
                  color: isSelected ? "var(--Blue-400)" : "var(--Neutral-90)",
                }),
                menu: (styles) => ({
                  ...styles,
                  backgroundColor: "var(--Neutral-10)",
                  border: "1px solid var(--Neutral-30)",
                }),
              }}
              value={selectedOption}
              options={filterOptions}
              onChange={(option) => setSelectedOption(option)}
              isSearchable={false}
            />
          </div>

          <div className="search-section">
            <label htmlFor="log-search" className="control-label">
              Search Logs:
            </label>
            <div className="search-input-container">
              <RiSearchLine className="search-icon" />
              <input
                id="log-search"
                type="text"
                className="search-input"
                placeholder="Search in logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSearch}
                  aria-label="Clear search"
                  className="clear-search-btn"
                >
                  <RiCloseLine />
                </Button>
              )}
            </div>
          </div>

          <div className="action-section">
            <Button
              variant="secondary"
              size="sm"
              onClick={clearLogs}
              disabled={logs.length === 0}
              leftIcon={
                <span className="material-symbols-outlined">delete</span>
              }
            >
              Clear Logs
            </Button>
          </div>
        </div>
      </section>

      {/* Logger Container */}
      <div className="logger-container" ref={loggerRef}>
        <Logger
          filter={(selectedOption?.value as LoggerFilterType) || "none"}
        />
        {filteredLogs.length === 0 && searchQuery && (
          <div className="no-results">
            <span className="material-symbols-outlined">search_off</span>
            <p>No logs found matching "{searchQuery}"</p>
            <Button variant="ghost" size="sm" onClick={clearSearch}>
              Clear search
            </Button>
          </div>
        )}
      </div>

      {/* Input Section */}
      <div className={cn("input-section", { disabled: !connected })}>
        <div className="input-header">
          <label htmlFor="message-input" className="input-label">
            Send Message to AI:
          </label>
          <span className="input-hint">
            {connected
              ? "Press Enter to send, Shift+Enter for new line"
              : "Connect to start chatting"}
          </span>
        </div>

        <div className="input-container">
          <textarea
            id="message-input"
            className="message-input"
            ref={inputRef}
            onKeyDown={handleKeyDown}
            onChange={(e) => setTextInput(e.target.value)}
            value={textInput}
            placeholder={
              connected ? "Type your message..." : "Connect to start chatting"
            }
            disabled={!connected}
            rows={2}
          />

          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!connected || !textInput.trim()}
            aria-label="Send message"
            className="send-button"
            leftIcon={<span className="material-symbols-outlined">send</span>}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
